import type {AttributeDefinition} from '@/types/attribute-definition'
import type {AttributeLayoutField, FormLayout, LayoutField} from '@/types/form-layout'
import type {SetAttributeFormValue} from '../types'

/**
 * Compute the set of party attribute definitionIds that the layout marks as
 * effectively required for the given partyType. A field is required when:
 *   - the layout pins {@code requiredOverride: 'required'}, OR
 *   - the layout leaves it as {@code 'inherit'} (or unset) AND the
 *     definition itself carries {@code required: true} for this partyType.
 * {@code 'optional'} always wins over the definition's required flag.
 */
function effectiveRequiredAttributeNames(
    layout: FormLayout | null | undefined,
    definitions: ReadonlyArray<AttributeDefinition>,
): Set<string> {
    const required = new Set<string>()
    if (!layout) return required
    const defById = new Map(definitions.map(d => [d.id, d]))
    for (const section of layout.layoutSchema.sections) {
        for (const field of section.fields as LayoutField[]) {
            if (field.source !== 'attribute') continue
            const attrField = field as AttributeLayoutField
            if (attrField.hidden) continue
            const def = defById.get(attrField.definitionId)
            if (!def || !def.active) continue
            const override = attrField.requiredOverride ?? 'inherit'
            if (override === 'optional') continue
            if (override === 'required' || def.required) {
                required.add(def.name)
            }
        }
    }
    return required
}

/**
 * Returns true when every layout-required attribute carries a non-blank value.
 *
 * <p>Used by {@code OnboardingWizard} to gate Next on both the ORGANIZATION
 * step (party attributes) and the IDENTITY step (identity attributes) —
 * without this check the wizard would let the user submit a payload the
 * backend rejects with PT0001/AR0001 (400) because the displayName template
 * can't resolve, leaving the detail page with a blank derived display name
 * (issue 1028).
 */
export function requiredAttrsAreFilled(
    values: ReadonlyArray<SetAttributeFormValue>,
    layout: FormLayout | null | undefined,
    definitions: ReadonlyArray<AttributeDefinition>,
): boolean {
    const required = effectiveRequiredAttributeNames(layout, definitions)
    if (required.size === 0) return true
    const valueByName = new Map(values.map(v => [v.attributeName, v]))
    for (const name of required) {
        const v = valueByName.get(name)
        if (!v || !v.valueString || v.valueString.trim().length === 0) {
            return false
        }
    }
    return true
}

